import { Component, OnInit, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import * as fromRoot from '@tv3/store/state'
import { MatPaginator, PageEvent } from '@angular/material/paginator'
import { MatTableDataSource } from '@angular/material/table'
import { MatSort } from '@angular/material/sort'
import {
  ActionFailed,
  Destroyable,
  DialogService,
  Rental,
  selectAllRentals,
  selectRentalEntities,
  Toaster,
  untilDestroy,
} from '@tokeet-frontend/tv3-platform'
import { isEmptyTable, localeCompareSort } from '@tokeet-frontend/tv3-platform'
import { azFilter } from '@tv3/constants/a-z-filter'
import {
  ChannelReviewItem,
  ChannelReviewSearchParams,
  ReviewRatingCategory,
  channelReviewSourceOptions,
  getChannelMaxRating,
  ReviewService,
} from '@tokeet-frontend/reviews'
import { FormBuilder } from '@angular/forms'
import * as lodash from 'lodash'
import { startWith, debounceTime, map, finalize, delay, switchMap } from 'rxjs/operators'
import { ReviewReplyDialogComponent } from '../components/review-reply-dialog/review-reply-dialog.component'
import { ReviewAutomationsOverlayComponent } from '@tv3/containers/automations/review/automations-overlay/review-automations-overlay.component'
import { ChannelHelperService } from '@tv3/containers/channels/channel-connect/channel-helper.service'
import {
  OpenInquiryOverlay,
  OpenGuestOverlay,
  OpenRentalOverlay,
  OpenDraftTasksApprovalDialog,
} from '@tv3/store/overlay/overlay.actions'
import { FetchGuests } from '@tv3/store/guest/guest.actions'
import { Guest } from '@tv3/store/guest/guest.model'
import { selectGuestEntities } from '@tv3/store/guest/guest.selectors'
import { ExportReviewDialogService } from '../components/export-dialog/export-reviews-dialog.service'
import { AmplitudeService } from '@tv3/services/amplitude.service'
import { selectDraftTasks } from '@tokeet-frontend/tasks'
import { ApproveDraftTasksDialogTabs } from '@tv3/containers/tasks/approve-draft-tasks-dialog/approve-draft-tasks-dialog.component'

@Component({
  selector: 'app-reviews-table',
  templateUrl: './reviews-table.component.html',
  styleUrls: ['./reviews-table.component.scss'],
})
export class ReviewsTableComponent extends Destroyable implements OnInit {
  @ViewChild('paginator', { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  displayedColumns = ['select', 'reviewer.name', 'submittedAt', 'overallRating', 'source', 'text', 'action']

  nameOptions = Object.assign([], azFilter)
  dataSource = new MatTableDataSource<ChannelReviewItem>()
  isEmpty$ = isEmptyTable(this.dataSource)
  isLoading = true
  isReviewEmpty = true

  sourceOptions = channelReviewSourceOptions

  filters: ChannelReviewSearchParams = {}
  totalCount = 0
  pagination = {
    pageIndex: 0,
    pageSize: 10,
  }
  filtersForm = this.fb.group({
    sources: [[]],
    rentals: [[]],
    submitted: [],
    includeDeletedInquiries: [false],
    responded: [],
    category: [],
    ratings: [[]],
    reviewerRole: [],
  })

  rentals$ = this.store.pipe(select(selectAllRentals))

  expandedReviewId: string
  rentalsById: Record<string, Rental> = {}
  guestsById: Record<string, Guest> = {}

  pendingTasks$ = this.store.pipe(select(selectDraftTasks))
  ratingCategoryOptions: { value: string; name: string }[] = []
  ratingValueOptions = [5, 4, 3, 2, 1].map((value) => ({
    value,
    name: value === 1 ? `${value} Star` : `${value} Stars`,
  }))
  reviewerRolesOptions = [
    { value: 'host', name: $localize`Host` },
    { value: 'guest', name: $localize`Guest` },
  ]

  constructor(
    private fb: FormBuilder,
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private reviewService: ReviewService,
    private channelHelperService: ChannelHelperService,
    private exportDialog: ExportReviewDialogService,
    private dialog: DialogService,
    private amplitudeService: AmplitudeService
  ) {
    super()
  }

  ngOnInit() {
    // this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort

    this.store.pipe(select(selectRentalEntities), untilDestroy(this)).subscribe((rentalsById) => {
      this.rentalsById = rentalsById
    })
    this.store.pipe(select(selectGuestEntities), untilDestroy(this)).subscribe((guestsById) => {
      this.guestsById = guestsById
    })

    this.loadRatingCategories()

    this.filtersForm.valueChanges
      .pipe(
        startWith(this.filtersForm.getRawValue()),
        debounceTime(1000),
        map(({ submitted, category, ratings, ...filters }) => {
          const normalizedFilters: Record<string, any> = {
            ...filters,
            submittedAfter: submitted?.from,
            submittedBefore: submitted?.to,
          }

          if (category && ratings?.length) {
            normalizedFilters.categoryRating = {
              category,
              ratings,
            }
          } else if (ratings?.length) {
            normalizedFilters.overallRating = ratings
          }

          const sanitizedFilters = lodash.fromPairs(
            lodash
              .toPairs(normalizedFilters)
              .filter(([k, v]) => !lodash.isNil(v) && (lodash.isArray(v) ? !!v.length : true))
          )

          return { ...sanitizedFilters }
        }),
        untilDestroy(this)
      )
      .subscribe((filters) => {
        this.filters = filters
        this.paginator.firstPage()
        this.onRefresh()
      })
  }

  private loadRatingCategories() {
    this.reviewService
      .getRatingCategories()
      .pipe(untilDestroy(this))
      .subscribe((options: ReviewRatingCategory[]) => {
        this.ratingCategoryOptions = lodash.sortBy(options, (option) => option.name?.toLowerCase() || option.name || '')
      })
  }

  onPageChange(data: PageEvent) {
    this.pagination = data
    this.onRefresh()
  }

  onRefresh() {
    this.reviewService
      .searchReviews({
        ...this.filters,
        includeStats: false,
        page: this.pagination.pageIndex + 1,
        limit: this.pagination.pageSize,
      })
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe(
        (data) => {
          this.dataSource.data = data.reviews
          this.totalCount = data?.counts?.total || 0
          if (this.isReviewEmpty) {
            this.isReviewEmpty = !this.totalCount
          }
          if (data.reviews?.length) {
            const guestIds = lodash.uniq(lodash.compact(lodash.map(data.reviews, (t) => t.guestId)))
            this.store.dispatch(FetchGuests({ ids: guestIds }))
          }
        },
        (error) => {
          this.store.dispatch(ActionFailed({ error }))
        }
      )
  }

  onImport() {
    this.reviewService.importReviews({ connectedProperties: true }).subscribe(
      () => {
        this.toaster.success($localize`Reviews imported successfully.`)
        this.onRefresh()
      },
      (error) => {
        this.store.dispatch(ActionFailed({ error }))
      }
    )
  }

  getMaxRating(item: ChannelReviewItem) {
    return getChannelMaxRating(item.source)
  }

  onSearch(term: string) {
    this.paginator.firstPage()
    this.dataSource.filter = (term + '').trim().toLowerCase()
  }

  onExpandRow(item: ChannelReviewItem) {
    this.expandedReviewId = this.expandedReviewId === item.reviewId ? null : item.reviewId
  }

  onReview(review: ChannelReviewItem) {}

  canReply(review: ChannelReviewItem) {
    return review.reviewer?.role !== 'host' && !review.response && review.isEligibleForResponse
  }

  onReply(item: ChannelReviewItem) {
    this.dialog
      .openVariableDialog(ReviewReplyDialogComponent, {
        width: '800px',
        data: { review: item },
      })
      .afterClosed()
      .pipe(switchMap(() => this.reviewService.getReview(item._id)))
      .subscribe((review) => {
        this.dataSource.data = this.dataSource.data.map((t) => (t._id === review._id ? review : t))
      })
  }

  onTogglePublish(item: ChannelReviewItem) {
    this.reviewService.updateReview(item._id, { published: !item.published }).subscribe(
      (review) => {
        this.toaster.success(
          $localize`Review ` +
            (review.published ? $localize`published` : $localize`unpublished`) +
            $localize` successfully`
        )
      },
      (error) => this.store.dispatch(ActionFailed({ error }))
    )
  }

  onBooking(inquiryId: string) {
    this.store.dispatch(OpenInquiryOverlay({ inquiryId }))
  }

  onGuest(guestId: string) {
    this.store.dispatch(OpenGuestOverlay({ guestId }))
  }

  onRental(rentalId: string) {
    this.store.dispatch(OpenRentalOverlay({ rentalId }))
  }

  onAddChannel() {
    this.channelHelperService.connectChannel()
  }

  onAddAutomation() {
    this.dialog.openDrawer(ReviewAutomationsOverlayComponent, { panelClass: 'drawer-full-70' })
  }

  onDownload() {
    const items = this.dataSource.filteredData
    this.exportDialog
      .open(items)
      .afterClosed()
      .subscribe(() => {
        this.amplitudeService.logEvent('download-review-list')
      })
  }

  onDraftTasks() {
    this.store.dispatch(OpenDraftTasksApprovalDialog({ data: { tab: ApproveDraftTasksDialogTabs.Review } }))
  }
}
